import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';

import { useSmoothScrollTo } from '../hooks/useSmoothScrollTo';

import { GREEN, CHARCOAL, labelMono } from '../../theme/tokens2';

// Explore: each link scrolls to a section of the homepage (by its id).
const NAV_LINKS = [
  { label: '24-Hour Sourcing & Physical Samples', id: 'sourcing' },  // Section 02
  { label: 'All-in-One Planning',                 id: 'planning'        },  // Section 03
  { label: 'Start Planning',                      id: 'final-cta'       },  // Section 05
];

// Resources: the live pages from the Header's "Resources" dropdown.
// Only add pages that are live (no "Coming Soon" items).
const RESOURCE_LINKS = [
  { label: 'Singapore Showroom Map', to: '/showrooms' },
  { label: 'Inspiration Accounts',   to: '/socials'   },
];

const INFO_LINKS = [
  { label: 'Terms Of Use', to: '/termsofuse' },
  { label: 'Privacy',      to: '/privacy'    },
];

const INSTAGRAM_URL = 'https://instagram.com/savvyrenovator';

// Organisations that support Savvy Renovator, listed under "Supported by".
// To add one, add a line here.
const SUPPORTERS = [
  { name: 'Google for Startups Cloud Program',
    url: 'https://cloud.google.com/startup' },
  { name: 'Action Community for Entrepreneurship (ACE.SG)',
    url: 'https://ace.sg/' },
];

// ─── Small helper for hover-able dark text buttons ───────────────────────────
function FooterButton({ children, onClick, to }) {
  const [hovered, setHovered] = useState(false);
  const isLink = !!to;
  return (
    <Box
      component={isLink ? Link : 'button'}
      to={isLink ? to : undefined}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'block',
        background: 'none', border: 'none', cursor: 'pointer',
        p: 0, textAlign: 'left',
        textDecoration: 'none',
        fontSize: '0.875rem',
        lineHeight: 1.5,   // so long links that wrap onto two lines stay easy to read
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: hovered ? '#fff' : 'rgba(255,255,255,0.50)',
        transition: 'color 0.2s ease',
      }}
    >
      {children}
    </Box>
  );
}

// ─── Small helper for the hover-able social icon ─────────────────────────────
function SocialIcon({ href, label, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: hovered ? '#fff' : 'rgba(255,255,255,0.50)',
        transition: 'color 0.2s ease',
      }}
    >
      {children}
    </Box>
  );
}

// ─── Link to a supporting organisation ───────────────────────────────────────
// Looks like the other footer links, opens in a new tab, and ends with a small ↗.
// The last word and the ↗ never split across lines, so when a long name wraps,
// the arrow always stays next to the last word.
function SupporterLink({ name, url }) {
  const [hovered, setHovered] = useState(false);
  const words    = name.split(' ');
  const lastWord = words.pop();
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'block',
        textDecoration: 'none',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: hovered ? '#fff' : 'rgba(255,255,255,0.50)',
        transition: 'color 0.2s ease',
        '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 4 },
      }}
    >
      {words.length > 0 && `${words.join(' ')} `}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {lastWord}{'\u00A0'}<span aria-hidden="true">↗</span>
      </Box>
      {/* Read out by screen readers only */}
      <Box component="span" sx={{
        position: 'absolute', width: 1, height: 1, overflow: 'hidden',
        clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
      }}>
        {' '}(opens in a new tab)
      </Box>
    </Box>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────
export default function Footer() {
  const [topHovered, setTopHovered] = useState(false);

  const scrollTo = useSmoothScrollTo();
  
  return (
    <Box
      component="footer"
      sx={{ backgroundColor: CHARCOAL, position: 'relative', overflow: 'hidden' }}
    >
      {/* Subtle dark grid */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1400, mx: 'auto' }}>

        {/* ── Main grid ── */}
        <Box sx={{
          display: 'grid',
          // Mobile (narrower than 900px): one column. Desktop: all four columns side by side.
          gridTemplateColumns: { xs: '1fr', md: '4fr 3fr 3fr 3fr' },
          borderBottom: '1px solid rgba(255,255,255,0.10)',
        }}>

          {/* Brand block */}
          <Box sx={{
            px: { xs: 3, md: 6 }, pt: 7, pb: 5,
            borderRight:  { xs: 'none', md: '1px solid rgba(255,255,255,0.10)' },
            borderBottom: { xs: '1px solid rgba(255,255,255,0.10)', md: 'none' },
          }}>
            {/* Logo */}
            <Typography sx={{ ...labelMono, color: '#fff', mb: 3 }}>Savvy Renovator</Typography>

            <Typography sx={{
              fontSize: '0.875rem', color: 'rgba(255,255,255,0.40)',
              lineHeight: 1.65, maxWidth: 280,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}>
              A platform that helps homeowners plan renovations with clarity, confidence,
              and control—before committing to any renovation firm.
            </Typography>

            <Typography sx={{
              mt: 3,
              fontSize: '0.875rem', color: 'rgba(255,255,255,0.40)',
              lineHeight: 1.65, maxWidth: 280,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}>
              hello@savvyrenovator.com
            </Typography>

            <Typography sx={{ mt: 3, ...labelMono, color: GREEN }}>
              Plan smart. Renovate savvy.
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <SocialIcon href={INSTAGRAM_URL} label="Savvy Renovator on Instagram">
                <InstagramIcon sx={{ fontSize: '1.25rem' }} />
              </SocialIcon>
            </Box>
          </Box>

          {/* Explore column */}
          <Box sx={{
            px: { xs: 3, md: 6 }, py: 7,
            borderRight:  { xs: 'none', md: '1px solid rgba(255,255,255,0.10)' },
            borderBottom: { xs: '1px solid rgba(255,255,255,0.10)', md: 'none' },
          }}>
            <Typography sx={{ ...labelMono, color: 'rgba(255,255,255,0.30)', mb: 3 }}>
              Explore
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {NAV_LINKS.map(({ label, id }) => (
                <FooterButton key={label} onClick={() => scrollTo(id)}>
                  {label}
                </FooterButton>
              ))}
              <FooterButton to={'/signin'}>Sign In</FooterButton>
            </Box>
          </Box>

          {/* Resources column */}
          <Box sx={{
            px: { xs: 3, md: 6 }, py: 7,
            borderRight:  { xs: 'none', md: '1px solid rgba(255,255,255,0.10)' },
            borderBottom: { xs: '1px solid rgba(255,255,255,0.10)', md: 'none' },
          }}>
            <Typography sx={{ ...labelMono, color: 'rgba(255,255,255,0.30)', mb: 3 }}>
              Resources
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {RESOURCE_LINKS.map(({ label, to }) => (
                <FooterButton key={label} to={to}>{label}</FooterButton>
              ))}
            </Box>
          </Box>

          {/* Info column */}
          <Box sx={{
            px: { xs: 3, md: 6 }, pt: 7, pb: 5,
            display: 'flex', flexDirection: 'column',
          }}>
            <Typography sx={{ ...labelMono, color: 'rgba(255,255,255,0.30)', mb: 3 }}>
              Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {INFO_LINKS.map(({ label, to }) => (
                <FooterButton key={label} to={to}>{label}</FooterButton>
              ))}
            </Box>

            {/* Supported by */}
            <Typography sx={{ ...labelMono, color: 'rgba(255,255,255,0.30)', mt: 6, mb: 3 }}>
              Supported by
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SUPPORTERS.map(({ name, url }) => (
                <SupporterLink key={name} name={name} url={url} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* ── Bottom strip ── */}
        <Box sx={{
          px: { xs: 3, md: 6 }, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Typography sx={{ ...labelMono, color: 'rgba(255,255,255,0.20)' }}>
            © {new Date().getFullYear()} Savvy Renovator
          </Typography>
          <Box
            component="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            onMouseEnter={() => setTopHovered(true)}
            onMouseLeave={() => setTopHovered(false)}
            sx={{
              ...labelMono,
              color: topHovered ? '#fff' : 'rgba(255,255,255,0.30)',
              background: 'none', border: 'none', cursor: 'pointer', p: 0,
              transition: 'color 0.2s ease',
            }}
          >
            Back to top ↑
          </Box>
        </Box>
      </Box>
    </Box>
  );
}